import {
  getRequiredPayslipPeriods,
  isPayslipWithinRequiredPeriod,
} from "../helper/common";

// Employment statuses where 3 recent payslips are compulsory.
// Mirrors DocumentSection IncomeTaxesSection `isThreePayslipRequired`.
export const isThreePayslipEmploymentStatus = (employmentStatus) =>
  employmentStatus === "employed" || employmentStatus === "public_servant";

export const PAYSLIP_ORDER = [
  "1st month payslip",
  "2nd month payslip",
  "3rd month payslip",
];

// Build the flat forensic documents_list array from a user's forensic analysis,
// same source IncomeTaxesSection uses for period matching.
export const getForensicDocumentsList = (user) => {
  const reports =
    user?.tenant_extra_id?.forensic_analysis?.profileReports ??
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ??
    [];
  return (Array.isArray(reports) ? reports : []).flatMap(
    (entry) => entry?.report?.documents_list || []
  );
};

// Extract { period_year, period_month, date, created_at } for a payslip doc,
// replicating IncomeTaxesSection slot-matching extraction order.
export const getPayslipPeriodForDoc = (
  doc,
  forensicList = [],
  forensicByFile = {}
) => {
  const forensicData =
    (Array.isArray(forensicList) ? forensicList : []).find(
      (entry) =>
        entry?.instanceId &&
        doc?.documentAnalysisData?.instanceId &&
        entry.instanceId === doc.documentAnalysisData.instanceId
    ) ?? forensicByFile?.[doc?.file_name];

  const period_year = Number(
    forensicData?.data?.period_year ??
      doc?.documentAnalysisData?.data?.period_year ??
      doc?.documentAnalysisData?.period_year ??
      doc?.period_year
  );
  const period_month = Number(
    forensicData?.data?.period_month ??
      doc?.documentAnalysisData?.data?.period_month ??
      doc?.documentAnalysisData?.period_month ??
      doc?.period_month
  );

  return {
    period_year: Number.isFinite(period_year) ? period_year : undefined,
    period_month: Number.isFinite(period_month) ? period_month : undefined,
    date: doc?.date,
    created_at: doc?.created_at,
  };
};

const getUniqueDocKey = (doc, index = 0) =>
  doc?.file_name ?? doc?._id ?? doc?.id ?? `payslip-${index}`;

// Greedy 1:1 match of payslip docs to required periods (same as display slots).
// Returns { filled, missingPeriods }.
export const matchPayslipSlots = (
  payslipDocs = [],
  { forensicList = [], forensicByFile = {} } = {},
  requiredPeriods = []
) => {
  const docs = Array.isArray(payslipDocs) ? payslipDocs : [];
  const periods = Array.isArray(requiredPeriods) ? requiredPeriods : [];
  const usedKeys = new Set();
  const missingPeriods = [];

  periods.forEach((period) => {
    const matchingDoc = docs.find((doc, index) => {
      const key = getUniqueDocKey(doc, index);
      if (usedKeys.has(key)) return false;
      const payslipObj = getPayslipPeriodForDoc(
        doc,
        forensicList,
        forensicByFile
      );
      const matched = isPayslipWithinRequiredPeriod(payslipObj, [period]);
      if (matched) usedKeys.add(key);
      return matched;
    });
    if (!matchingDoc) missingPeriods.push(period);
  });

  return { filled: periods.length - missingPeriods.length, missingPeriods };
};

// Slot-aware filled count for the last-3-months requirement.
// Falls back to [] periods when not applicable.
export const countFilledPayslipSlots = (
  allDocs = [],
  { user = null, forensicRawData = null, forensicByFile = {} } = {}
) => {
  const payslipDocs = (Array.isArray(allDocs) ? allDocs : []).filter((doc) =>
    (doc?.type ?? "").toLowerCase().includes("payslip")
  );
  const requiredPeriods = getRequiredPayslipPeriods();
  const forensicList = Array.isArray(forensicRawData)
    ? forensicRawData.flatMap((entry) => entry?.report?.documents_list || [])
    : getForensicDocumentsList(user);
  return {
    payslipDocs,
    requiredPeriods,
    ...matchPayslipSlots(
      payslipDocs,
      { forensicList, forensicByFile },
      requiredPeriods
    ),
  };
};
